import React, { useState } from 'react';
import { Image, Film, Download, ExternalLink, Clock, Loader2, CheckCircle2, XCircle } from 'lucide-react';
import type { MediaGenerateResponse, MediaResult } from '@/types/agent';

const parseAspectRatio = (ratio?: string): number => {
  if (!ratio) return 1;
  const parts = ratio.split(':');
  if (parts.length === 2) {
    const w = parseFloat(parts[0]);
    const h = parseFloat(parts[1]);
    if (w > 0 && h > 0) return w / h;
  }
  return 1;
};

const StatusBadge: React.FC<{ status: string }> = ({ status }) => {
  if (status === 'SUCCESS') {
    return (
      <span className="flex items-center gap-1 text-[10px] text-green-400">
        <CheckCircle2 size={10} />
        完成
      </span>
    );
  }
  if (status === 'PENDING' || status === 'RUNNING' || status === 'QUEUED') {
    return (
      <span className="flex items-center gap-1 text-[10px] text-yellow-400">
        <Loader2 size={10} className="animate-spin" />
        生成中
      </span>
    );
  }
  if (status === 'FAILED' || status === 'ERROR') {
    return (
      <span className="flex items-center gap-1 text-[10px] text-red-400">
        <XCircle size={10} />
        失败
      </span>
    );
  }
  return <span className="text-[10px] text-gray-500">{status}</span>;
};

interface ImageResultCardProps {
  result: MediaResult;
  aspectRatio?: string;
}

const ImageResultCard: React.FC<ImageResultCardProps> = ({ result, aspectRatio }) => {
  const isDataUrl = result.url.startsWith('data:');
  const isLocalUrl = result.url.startsWith('/assets/') || result.url.startsWith('http');
  const [naturalRatio, setNaturalRatio] = useState<number | null>(null);

  const cssRatio = naturalRatio ?? parseAspectRatio(aspectRatio);

  return (
    <div className="group relative rounded-lg border border-white/5 bg-white/[0.02] overflow-hidden">
      <div className="relative w-full bg-black/20" style={{ aspectRatio: `${cssRatio}` }}>
        {(isDataUrl || isLocalUrl) && (
          <img
            src={result.url}
            alt="Generated"
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
            onLoad={(e) => {
              const img = e.currentTarget;
              if (img.naturalWidth > 0 && img.naturalHeight > 0) {
                setNaturalRatio(img.naturalWidth / img.naturalHeight);
              }
            }}
          />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
        <div className="absolute bottom-2 right-2 flex gap-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
          <a
            href={result.url}
            download
            className="p-1.5 rounded-md bg-black/60 text-white hover:bg-black/80 transition-colors"
            onClick={(e) => {
              if (isDataUrl) {
                const link = e.currentTarget;
                link.href = result.url;
                link.download = `bongai-image.${result.outputType || 'png'}`;
              }
            }}
          >
            <Download size={12} />
          </a>
          {isLocalUrl && (
            <a
              href={result.url}
              target="_blank"
              rel="noopener noreferrer"
              className="p-1.5 rounded-md bg-black/60 text-white hover:bg-black/80 transition-colors"
            >
              <ExternalLink size={12} />
            </a>
          )}
        </div>
      </div>
    </div>
  );
};

interface VideoResultCardProps {
  result: MediaResult;
  metadata?: MediaGenerateResponse['videoMetadata'];
  aspectRatio?: string;
}

const VideoResultCard: React.FC<VideoResultCardProps> = ({ result, metadata, aspectRatio }) => {
  const cssRatio = parseAspectRatio(aspectRatio);

  return (
    <div className="group relative rounded-lg border border-white/5 bg-white/[0.02] overflow-hidden">
      <div
        className="relative w-full bg-black/20"
        style={{ aspectRatio: aspectRatio ? `${cssRatio}` : undefined }}
      >
        {result.url && (
          <video
            src={result.url}
            controls
            className="w-full h-full object-cover"
            preload="metadata"
          />
        )}
        {!result.url && (
          <div className="flex items-center justify-center w-full h-full">
            <Film size={24} className="text-gray-600" />
          </div>
        )}
        {metadata?.duration && (
          <span className="absolute bottom-2 right-2 flex items-center gap-1 px-1.5 py-0.5 rounded bg-black/70 text-[10px] text-white">
            <Clock size={10} />
            {typeof metadata.duration === 'number' ? `${metadata.duration}s` : metadata.duration}
          </span>
        )}
      </div>
    </div>
  );
};

interface MediaResultCardProps {
  data: MediaGenerateResponse;
}

export const MediaResultCard: React.FC<MediaResultCardProps> = ({ data }) => {
  const isImage = data.taskType === 'image';
  const isVideo = data.taskType === 'video';

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2 text-xs text-gray-400">
        <span className="px-1.5 py-0.5 rounded bg-white/5 text-[10px] font-medium">
          {isImage ? '🖼️ 生图' : '🎬 生视频'}
        </span>
        <span className="truncate max-w-[200px]">「{data.prompt}」</span>
        <span className="text-gray-600">·</span>
        <StatusBadge status={data.status} />
      </div>

      {isImage && data.results.length > 0 && (
        <div className={`grid gap-2 ${data.results.length > 1 ? 'grid-cols-2' : 'grid-cols-1'}`}>
          {data.results.map((result, idx) => (
            <ImageResultCard key={`${result.url}-${idx}`} result={result} aspectRatio={data.aspectRatio} />
          ))}
        </div>
      )}

      {isVideo && data.results.length > 0 && (
        <div className="space-y-2">
          {data.results.map((result, idx) => (
            <VideoResultCard key={`${result.url}-${idx}`} result={result} metadata={data.videoMetadata} aspectRatio={data.videoMetadata?.aspect_ratio} />
          ))}
        </div>
      )}

      {data.results.length === 0 && data.status !== 'SUCCESS' && (
        <div className="flex items-center justify-center py-6 rounded-lg border border-dashed border-white/10 bg-white/[0.02]">
          <div className="text-center">
            <Loader2 size={20} className="text-gray-600 animate-spin mx-auto mb-2" />
            <p className="text-[11px] text-gray-500">
              {isImage ? '图片' : '视频'}生成中，请稍候...
            </p>
          </div>
        </div>
      )}

      <div className="text-[10px] text-gray-600">
        模型: {data.modelId}
      </div>
    </div>
  );
};
